import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Button, CircularProgress, Divider, Grid, IconButton, styled } from "@material-ui/core";

// Components
import { basicButtonStyles } from "../../../Components/ui/buttons";
import SelectFilter from "../../Filters/SelectFilter";
import PriorityIndicator from "../../../Pages/BookAnalysis/PriorityIndicator/PriorityIndicator";
import ProspectsTotal from "../ProspectsTotal/ProspectsTotal";
import FiltersContianer from "../../Filters/FiltersContianer/FiltersContianer";
import TableContainer from "../../Prospects/TableContainer/TableContainer";

// Redux
import { selectActiveTab, selectProductsData, selectProspectsData, selectRuns, selectSelectedPolicies, setDurationFilter, setLapsePremiumFilter, setPremiumTermFilter, setProductNameFilter, setSensitivityFilter, setStoryTypeFilter, setUIFaceAmountFilter } from "../../../Pages/BookAnalysis/BookAnalysisSlice";
import { selectColumns, selectUserSubscriptions, setColumns } from "../../../AppSlice";
import { selectProspectsFilters, setAgeFilter, setAgentStatusFilter, setContractLossFilter, setContractValueFilter, setIncomeAccFilter, setIncomeLossFilter, setLIBRStatusFilter, setLapseFaceAmountFilter, setLibrFilter, setPriorityFilter, setProductFilter, setResStateFilter, setStateFilter, setStoryFilter, setSubActivityFilter, setSurrValueFilter, setSurrYearValueFilter, setUIPremiumFilter, setWithdravalActivityFilter } from "../../../Pages/BookAnalysis/BookAnalysisSlice";

// Services
import { getCountryCurrencySymbol, nFormatter } from "../../../global/helpers";
import { defaultAgeFilter, defaultAgeFilterLife, defaultAgentStatusFilter, defaultContractValueFilter, defaultLIBRStatusFilter, defaultLibrFilter, defaultOpportunityTypeFilter, defaultPercentage, defaultPriorityFilter, defaultYearsFilter } from "../constants";

import ColumnsPopup from "../../Popups/ColumnsPopup/ColumnsPopup";
import BookAnalysisService from "../../../Pages/BookAnalysis/BookAnalysisService";
import { useHistory } from "react-router-dom/cjs/react-router-dom";

// Icons
import StoryIcon from "../StoryIcon/StoryIcon";
import { ReactComponent as Arrow } from '../../../Assets/icon-arrow-right.svg';
import { ReactComponent as Delete } from '../../../Assets/icon-trashcan.svg';
import SearchIcon from '@mui/icons-material/Search';

import { User, checkPermission } from "../../../global/user";
import NumberFormat from "react-number-format";
import { useStyles } from "./styles";
import FormInput from "../../Forms/FormInput";
import DashboardService from "../../../Pages/Dashboard/DashboardService";
import { frozenGridColumnsPositions, frozenGridColumnsPositionsBroker } from "../../../global/constants";
import { setCampaignProspects } from "../../../Pages/CreateCampaign/CreateCampaignSlice";
import PriorityTag from "../../../Pages/BookAnalysis/Prospects/PriorityTag/PriorityTag";

export const UICreateButton = styled(Button)({
    ...basicButtonStyles,
    fontFamily: "Roboto",
    fontSize: 14,
    fontWeight: 500,
    lineHeight: "16px",
    padding: '13px 22px',
    height: 'auto',
    width: 'auto',
    maxWidth: 168,
    borderRadius: 8,
    backgroundColor: '#0BCB47',
    borderColor: '#FFF',
    color: '#FFF',
    '&:hover': {
      opacity: '0.85',
      backgroundColor: '#0BCB47',
      borderColor: '#FFF',
      boxShadow: 'none',
    },
    '&:active': {
      backgroundColor: '#0BCB47',
      borderColor: '#FFF',
      boxShadow: 'none'
    },
    '&.Mui-disabled': {
      opacity: '0.25',
      borderColor: '#FFF',
      boxShadow: 'none',
      color: '#FFFFFF'
    },
});

const ApiService = new BookAnalysisService()
const DashService = new DashboardService()

const ProspectsContainer = ({
    userType,
    data,
    headers,
    page,
    loaded,
    clear,
    sort,
    onChangePage,
    onFiltersChange,
    handleChangePage,
    handleSelectPolicy,
    handleSort,
    handleSearch,
    handleResetUIFilters,
    handleResetLapseFilters,
    handleExportAll,
    handleExport,
    renderRow,
    rowsPerPage,
    setRowsPerPage,
    divider, 
    divided = true, 
    mode = 'tab',
    frozenColumns = [],
    hiddenColumns = []
}) => {
    const dispatch = useDispatch()
    const history = useHistory()
    const currency = getCountryCurrencySymbol()
    const classes = useStyles()
  
    const [ defaultMarketLagFilter, setDefaultMarketLagFilter ] = useState({ "All": true })
    const [ defaultStateFilter, setDefaultStateFilter ] = useState({ "All": true })
    const [ defaultDurationFilter, setDefaultDurationFilter ] = useState({ "All": true })
    const [ defaultOpportunityFilter, setDefaultOpportunityFilter ] = useState({ "All": true })
    const [ defaultProductFilter, setDefaultProductFilter ] = useState({ "All": true })
    const [ defaultPremiumUIFilter, setDefaultPremiumUIFilter ] = useState({ "All": true, [`<${currency}500`]: false, [`<${currency}500-${currency}1500`]: false, [`<${currency}1500-${currency}2500`]: false, [`<${currency}2500-${currency}5K`]: false, [`<${currency}5K-${currency}10K`]: false, [`${currency}10K<`]: false })
    const [ defaultFaceAmountFilter, setDefaultFaceAmountFilter ] = useState({ "All": true })
    const [ openColumns, setOpenColumns ] = useState(false)
    const [ tmpColumns, setTmpColumns ] = useState(null)

    const summaryData = useSelector(selectProductsData)
    const runs = useSelector(selectRuns)
    const run_ids = [
      ...summaryData.lapse_runs?.map(({ run_id }) => run_id),
      ...summaryData.ui_runs?.map(({ run_id }) => run_id)
    ]
    const prospectsData = useSelector(selectProspectsData)
    const prospectsFilters = useSelector(selectProspectsFilters)
    const activeTab = useSelector(selectActiveTab)
    const columns = useSelector(selectColumns)
    const subscriptions = useSelector(selectUserSubscriptions)
    const selectedPolicies = useSelector(selectSelectedPolicies)

    const isAnnuities = localStorage.getItem("accountType") === "annuities"
    const isAE = (activeTab === "ProspectsAE" && mode === "tab") || isAnnuities
    const isBroker = (localStorage.getItem("accountType") === "broker" && User().isHideOnProd) || User().isBroker
    const isFreeSubcriptionPlan = isBroker && !subscriptions?.find(({ name }) => name === "basic")

    const onChangeColumns = (item, value) => {
      dispatch(setColumns({
        ...columns,
        [item]: value
      }))
      
    }

    const discardChangeColumns = () => {
      dispatch(setColumns(tmpColumns))
      setOpenColumns(false)
    }

    const applyColumns = () => {
      ApiService._patchRunColumn(
        isBroker ? runs.map(({ run_id }) => run_id) : run_ids,
        Object.keys(columns)?.filter((item) => columns[item]),
        () => {
          setTmpColumns(columns)
          setOpenColumns(false)
        },
        () => {}
      )
    }

    const handleCreateCampaignClick = () => {
      if (userType === "platform-anonym") {
        //setOpenRegisterDialog(true)
      } else {
        if (isBroker) {
          dispatch(setCampaignProspects(data.count))
        }
        history.push(`/NewCampaign?run_ids=${run_ids?.join(',')}`)
      }
    }

    const handleDelete = () => {
      Object.keys(selectedPolicies).forEach((id, index) => {
        DashService._deleteRecommendation(id, () => {
          if (index === Object.keys(selectedPolicies).length-1) {
            onFiltersChange()
          }
        }, () => {})
      })
    }

    useEffect(() => {
      return () => {
        setTmpColumns(null)
      }
    }, [])

    useEffect(() => {
        setTmpColumns(columns)
    }, [columns])

    useEffect(() => {
        onChangePage()
    }, [onChangePage])
  
    useEffect(() => {
        onFiltersChange()
    }, [onFiltersChange])

    useEffect(() => {
        if (Object.keys(data.uniqueStories).length > 0) {
          setDefaultOpportunityFilter(data.uniqueStories)
        }
        if (Object.keys(data.uniqueProducts).length > 0) {
          setDefaultProductFilter(data.uniqueProducts)
        }
        if (Object.keys(data.face_amount_ranges).length > 0) {
          setDefaultFaceAmountFilter(data.face_amount_ranges)
        }
        if (Object.keys(data.unique_states).length > 0) {
          setDefaultStateFilter(data.unique_states)
        }
        if (Object.keys(data.uniqueProducts).length > 0) {
          setDefaultMarketLagFilter(data.uniqueProductNames)
        }
        if (Object.keys(data.durationSegments).length > 0) {
          setDefaultDurationFilter(data.durationSegments)
        }
      }, [data]) 

    return (
        <>
            <Grid container xs={12} justifyContent="space-between" className={isBroker ? "" : ""}>

              <ProspectsTotal
                count={data.count}
                premium={data.total_premium}
                totals={false}
                disabled={!prospectsData?.count || history.location.search?.includes("demo") || !checkPermission('create_campaign')}
              />

              <FiltersContianer
                title="Existing Policy Data"
                size={isBroker ? "xs-2" : "xs"}
                onReset={handleResetUIFilters}
              >
                {
                  (summaryData?.lapse_runs?.length  || summaryData?.ui_runs?.length || isBroker) ? 
                  isAE ? 
                  <Grid container>
                      {columns["Product Type"] && <SelectFilter
                        title="Product Type"
                        popoverText="Product Type"
                        defaultValue={defaultProductFilter}
                        reset={clear}
                        disabled={isFreeSubcriptionPlan}
                        resetFilter={
                          () => {
                            dispatch(setProductFilter(defaultProductFilter))
                          }
                        }
                        onChange={(value) => {
                          dispatch(setProductFilter(value))
                        }}
                        dynamic
                        />}
                            
                      {columns["Age"] && <SelectFilter
                        title="Age"
                        popoverText="Age"
                        defaultValue={defaultAgeFilter}
                        reset={clear}
                        resetFilter={
                          (e) => {
                            dispatch(setAgeFilter(defaultAgeFilter))
                          }
                        }
                        onChange={(value) => {
                          dispatch(setAgeFilter(value))
                        }}
                        renderControl={
                          (item) => {
                            if(item[0] === "<") {
                              return `-50`
                            } else if(item.includes("<")) {
                            return `80+;`
                            } else {
                              return item
                            }
                          }
                        }
                        disabled={isFreeSubcriptionPlan}
                        />}

                      {columns["Contract Value"] && <SelectFilter
                        title="Contract Value"
                        popoverText="Contract Value"
                        defaultValue={defaultContractValueFilter}
                        reset={clear}
                        resetFilter={
                          () => {
                            dispatch(setContractValueFilter(defaultContractValueFilter))
                          }
                        }
                        onChange={(value) => {
                          dispatch(setContractValueFilter(value))
                        }}
                        disabled={isFreeSubcriptionPlan}
                        />}

                      {columns["Years Left in Surrender Charge"] && <SelectFilter
                        title="Years in Surrender Charge"
                        popoverText="Years in Surrender Charge"
                        defaultValue={defaultYearsFilter}
                        reset={clear}
                        resetFilter={() => dispatch(setSurrYearValueFilter(defaultYearsFilter))}
                        onChange={(value) => dispatch(setSurrYearValueFilter(value))}
                        disabled={isFreeSubcriptionPlan}
                        />} 

                      {columns["Surrender Value"] && <SelectFilter
                        title="Surrender Value"
                        popoverText="Surrender Value"
                        defaultValue={defaultContractValueFilter}
                        reset={clear}
                        resetFilter={() => dispatch(setSurrValueFilter(defaultContractValueFilter))}
                        onChange={(value) => { dispatch(setSurrValueFilter(value)) }}
                        disabled={isFreeSubcriptionPlan}
                        />}   

                      {columns["LIB Payment"] && <SelectFilter
                        title="LIBR Pmnt"
                        popoverText="LIBR Pmnt"
                        defaultValue={defaultLibrFilter}
                        reset={clear}
                        resetFilter={() => dispatch(setLibrFilter(defaultContractValueFilter))}
                        onChange={(value) => { dispatch(setLibrFilter(value)) }}
                        disabled={isFreeSubcriptionPlan}
                        />} 

                      {columns["Withdraw Activity"] && <SelectFilter
                        title="Withdraw Activity"
                        popoverText="Withdraw Activity"
                        defaultValue={defaultPriorityFilter}
                        reset={clear}
                        resetFilter={() => dispatch(setWithdravalActivityFilter(defaultPriorityFilter))}
                        onChange={(value) => { dispatch(setWithdravalActivityFilter(value)) }}
                        disabled={isFreeSubcriptionPlan}
                        />}

                      {columns["Subaccount activity"] && <SelectFilter
                        title="Subacct. Activity"
                        popoverText="Subacct. Activity"
                        defaultValue={defaultPriorityFilter}
                        reset={clear}
                        resetFilter={() => dispatch(setSubActivityFilter(defaultPriorityFilter))}
                        onChange={(value) => { dispatch(setSubActivityFilter(value)) }}
                        disabled={isFreeSubcriptionPlan}
                        />}                        

                      {columns["Income Account Value"] && <SelectFilter
                        title="Income Acc. Value"
                        popoverText="Income Acc. Value"
                        defaultValue={defaultContractValueFilter}
                        reset={clear}
                        resetFilter={() => dispatch(setIncomeAccFilter(defaultContractValueFilter))}
                        onChange={(value) => dispatch(setIncomeAccFilter(value))}
                        disabled={isFreeSubcriptionPlan}
                        />}   

                      {columns["LIB Rider Status"] && <SelectFilter
                        title="LIBR Status"
                        popoverText="LIBR Status"
                        defaultValue={defaultLIBRStatusFilter}
                        reset={clear}
                        resetFilter={() => dispatch(setLIBRStatusFilter(defaultLIBRStatusFilter))}
                        onChange={(value) => dispatch(setLIBRStatusFilter(value))}
                        disabled={isFreeSubcriptionPlan}
                        />}

                      {columns["Contract Loss %"] && <SelectFilter
                        title="Contract Loss"
                        popoverText="Contract Loss"
                        defaultValue={defaultPercentage}
                        reset={clear}
                        resetFilter={() => dispatch(setContractLossFilter(defaultPercentage))}
                        onChange={(value) => dispatch(setContractLossFilter(value))}
                        disabled={isFreeSubcriptionPlan}
                        />}                        

                      {columns["Income Loss %"] && <SelectFilter
                        title="Income Loss"
                        popoverText="Income Loss"
                        defaultValue={defaultPercentage}
                        reset={clear}
                        resetFilter={() => dispatch(setIncomeLossFilter(defaultPercentage))}
                        onChange={(value) => dispatch(setIncomeLossFilter(value))}
                        disabled={isFreeSubcriptionPlan}
                        />}    

                      {columns["Agent Status"] && <SelectFilter
                        title="Agent Status"
                        popoverText="Agent Status"
                        defaultValue={defaultAgentStatusFilter}
                        reset={clear}
                        resetFilter={() => dispatch(setAgentStatusFilter(defaultAgentStatusFilter))}
                        onChange={(value) => dispatch(setAgentStatusFilter(value))}
                        disabled={isFreeSubcriptionPlan}
                        />}   

                      {columns["Issue State"] && <SelectFilter
                        title="Issue state"
                        popoverText="Issue state"
                        defaultValue={defaultStateFilter}
                        reset={clear}
                        resetFilter={() => dispatch(setStateFilter(defaultStateFilter))}
                        onChange={(value) => dispatch(setStateFilter(value))}
                        disabled={isFreeSubcriptionPlan}
                        dynamic
                        />}  

                      {columns["Residency State"] && <SelectFilter
                        title="Resid. State"
                        popoverText="Resid. State"
                        defaultValue={defaultStateFilter}
                        reset={clear}
                        resetFilter={() => dispatch(setResStateFilter(defaultStateFilter))}
                        onChange={(value) => dispatch(setResStateFilter(value))}
                        disabled={isFreeSubcriptionPlan}
                        />}                      
                  </Grid> 
                  :
                  <Grid container>
                      {columns["Product Name"] && <SelectFilter
                        title="Product Name"
                        popoverText="Product Name"
                        defaultValue={defaultMarketLagFilter}
                        reset={clear}
                        resetFilter={
                          () => {
                            dispatch(setProductNameFilter(defaultMarketLagFilter))
                          }
                        }
                        onChange={(value) => {
                          dispatch(setProductNameFilter(value))
                        }}
                        disabled={isFreeSubcriptionPlan}
                        dynamic
                        />}

                      {columns["Product Type"] && <SelectFilter
                        title="Product Type"
                        popoverText="Product Type"
                        defaultValue={defaultProductFilter}
                        reset={clear}
                        resetFilter={
                          () => {
                            dispatch(setProductFilter(defaultProductFilter))
                          }
                        }
                        onChange={(value) => {
                          dispatch(setProductFilter(value))
                        }}
                        disabled={isFreeSubcriptionPlan}
                        dynamic
                        />}
                            
                      {columns["Current Age"] && <SelectFilter
                        title="Current Age"
                        popoverText="Current Age"
                        defaultValue={defaultAgeFilterLife}
                        reset={clear}
                        resetFilter={
                          (e) => {
                            dispatch(setAgeFilter(defaultAgeFilterLife))
                          }
                        }
                        onChange={(value) => {
                          dispatch(setAgeFilter(value))
                        }}
                        disabled={isFreeSubcriptionPlan}
                        />}
                      
                      {columns["Annual Premium"] && <SelectFilter 
                        title="Annual Premium"
                        popoverText="Annual Premium"
                        defaultValue={defaultPremiumUIFilter}
                        reset={clear}
                        resetFilter={
                          () => {
                            //dispatch(resetUIFilter()); 
                            dispatch(setUIPremiumFilter(defaultPremiumUIFilter));
                          }
                        }
                        onChange={(value) => {
                          dispatch(setUIPremiumFilter(value))
                        }}  
                        disabled={isFreeSubcriptionPlan}
                        />} 
                        
                      {columns["Face Amount"] && !prospectsData.hidden_filters?.includes("Face Amount") && <SelectFilter
                      title="Face Amount"
                      popoverText="face amount"
                      defaultValue={defaultFaceAmountFilter}
                      reset={clear}
                      resetFilter={
                        () => {
                          setDefaultFaceAmountFilter(defaultFaceAmountFilter)
                          dispatch(setUIFaceAmountFilter(defaultFaceAmountFilter))
                        }
                      }
                      onChange={(value) => {
                        dispatch(setUIFaceAmountFilter(value))
                      }}
                      disabled={isFreeSubcriptionPlan}
                      dynamic
                      />}
                      
                        <SelectFilter 
                          title="Policy Duration"
                          popoverText="Policy Duration"
                          defaultValue={defaultDurationFilter}
                          reset={clear}
                          resetFilter={
                            () => {
                              dispatch(setDurationFilter(defaultDurationFilter));
                            }
                          }
                          onChange={(value) => {
                            dispatch(setDurationFilter(value))
                          }} 
                          disabled={isFreeSubcriptionPlan}
                          dynamic 
                          />
                          
                    <SelectFilter
                        title="State"
                        popoverText="State"
                        defaultValue={defaultStateFilter}
                        reset={clear}
                        resetFilter={() => dispatch(setStateFilter(defaultStateFilter))}
                        onChange={(value) => dispatch(setStateFilter(value))}
                        disabled={isFreeSubcriptionPlan}
                        dynamic
                        />           
                  </Grid> 
                  :
                  <Grid container justifyContent="center" alignContent="center" style={{minHeight: 119}}>
                    <CircularProgress style={{color: "#363ED3",width:48,height:48}}/>
                  </Grid>  
                }
              </FiltersContianer>

              <FiltersContianer
                title={isBroker ? "Magnet Recommends"  : "Atidot Recommends"}
                size={isBroker ? "sm-2" : "sm"}
                onReset={handleResetLapseFilters}
                openSettings={() => setOpenColumns(true)}
              >
                {
                  (summaryData?.lapse_runs?.length  || summaryData?.ui_runs?.length || isBroker) ? 
                  isAE ? 
                  <Grid container>
                      {columns["Upside Story"] && <SelectFilter
                        title="Upside Story"
                        popoverText="Upside Story"
                        defaultValue={defaultOpportunityFilter}
                        reset={clear}
                        resetFilter={
                          () => {
                            dispatch(setStoryFilter(defaultOpportunityFilter))
                          }
                        }
                        onChange={(value) => {
                          dispatch(setStoryFilter(value))
                        }}
                        disabled={isFreeSubcriptionPlan}
                        dynamic
                        />}   

                      {columns["Priority"] && <SelectFilter
                        title="Priority"
                        popoverText="priority"
                        defaultValue={defaultPriorityFilter}
                        labeled={false}
                        icons={{
                          "high": <PriorityTag value="high"/>,
                          "medium": <PriorityTag value="medium"/>,
                          "low": <PriorityTag value="low"/>,
                        }}
                        reset={clear}
                        resetFilter={
                          () => {
                            dispatch(setPriorityFilter(defaultPriorityFilter))
                          }
                        }
                        onChange={(value) => {
                          dispatch(setPriorityFilter(value))
                        }}
                        disabled={isFreeSubcriptionPlan}
                        />}

                      {columns["AUM Upside"] && <SelectFilter
                        title="AUM Upside"
                        popoverText="AUM Upside"
                        defaultValue={defaultContractValueFilter}
                        reset={clear.recomended}
                        resetFilter={
                          () => {
                            dispatch(setLapsePremiumFilter(defaultContractValueFilter))
                          }
                        }
                        onChange={(value) => {
                          dispatch(setLapsePremiumFilter(value))
                        }}
                        disabled={isFreeSubcriptionPlan}
                        dynamic
                        />}                        
                  </Grid> 
                  : 
                  <Grid container>
                      {columns["Upside Story"] && <SelectFilter
                        title="Upside Story"
                        popoverText="Upside Story"
                        defaultValue={defaultOpportunityFilter}
                        reset={clear}
                        resetFilter={
                          () => {
                            dispatch(setStoryFilter(defaultOpportunityFilter))
                          }
                        }
                        onChange={(value) => {
                          dispatch(setStoryFilter(value))
                        }}
                        disabled={isFreeSubcriptionPlan}
                        dynamic
                        />}

                      <SelectFilter
                          title="Opportunity Type"
                          popoverText="Opportunity Type"
                          defaultValue={defaultOpportunityTypeFilter}
                          icons={{
                            "New Business": <StoryIcon storyType="ui"/>,
                            "Retention": <StoryIcon storyType="lapse"/>,
                          }}
                          reset={clear}
                          resetFilter={
                            () => {
                              dispatch(setStoryTypeFilter(defaultOpportunityTypeFilter))
                            }
                          }
                          onChange={(value) => {
                            dispatch(setStoryTypeFilter(value))
                          }}
                          multiSelect={false}
                          disabled={isFreeSubcriptionPlan}
                          dynamic
                          />                  

                      {columns["Probability to lapse"] && <SelectFilter
                        title="Probability to Lapse"
                        popoverText="Probability to Lapse"
                        defaultValue={defaultPriorityFilter}
                        icons={{
                          "high": <PriorityIndicator priority="high"/>,
                          "medium": <PriorityIndicator priority="mid"/>,
                          "low": <PriorityIndicator priority="low"/>,
                        }}
                        reset={clear}
                        resetFilter={
                          () => {
                            dispatch(setPriorityFilter(defaultPriorityFilter))
                          }
                        }
                        onChange={(value) => {
                          dispatch(setPriorityFilter(value))
                        }}
                        disabled={isFreeSubcriptionPlan}
                        />}              

                        {columns["Sensitivity"] && isBroker && <SelectFilter
                          title="Sensitivity"
                          popoverText="Sensitivity"
                          defaultValue={defaultPriorityFilter}
                          icons={{
                            "high": <PriorityIndicator priority="high"/>,
                            "medium": <PriorityIndicator priority="mid"/>,
                            "low": <PriorityIndicator priority="low"/>,
                          }}
                          reset={clear}
                          resetFilter={
                            () => {
                              dispatch(setSensitivityFilter(defaultPriorityFilter))
                            }
                          }
                          onChange={(value) => {
                            dispatch(setSensitivityFilter(value))
                          }}
                          disabled={isFreeSubcriptionPlan}
                          />}
                      
                      {columns["Premium Upside"] && <SelectFilter
                        title="Premium Upside"
                        popoverText="Premium Upside"
                        defaultValue={defaultContractValueFilter}
                        reset={clear.recomended}
                        resetFilter={
                          () => {
                            dispatch(setPremiumTermFilter(defaultContractValueFilter))
                          }
                        }
                        onChange={(value) => {
                          dispatch(setPremiumTermFilter(value))
                        }}
                        disabled={isFreeSubcriptionPlan}
                        dynamic
                        />}  
                  </Grid>
                  :
                  <Grid container justifyContent="center" alignContent="center" style={{minHeight: 119}}>
                    <CircularProgress style={{color: "#363ED3",width:48,height:48}}/>
                  </Grid>
                }
              </FiltersContianer>

              {
                isBroker &&
                <Grid container alignItems="center" justifyContent="space-between" className={classes.totalsContainer}>
                  <Grid container alignItems="center" xs={6}>
                    <p className="contacts-text">
                      <NumberFormat value={data.count} displayType="text" thousandSeparator/> Contacts
                    </p> 
                    <Arrow className={classes.totalsArrow}/>
                    <p className="premium-text">
                      {nFormatter(data.total_premium, 2, currency)} Premium
                    </p>
                    <Divider orientation="vertical"/>
                    <IconButton onClick={handleDelete}><Delete/> <p>Archive</p></IconButton>
                  </Grid>

                  <Grid container className={classes.searchContainer}>
                    <FormInput
                      className={classes.searchBox}
                      placeholder="Search by ID, name, email"
                      onChange={handleSearch}
                      InputProps={{
                        startAdornment: (
                          <SearchIcon/>
                        )
                      }}
                      validate={false}
                      disabled={isFreeSubcriptionPlan}
                    />
                    <div>
                      <UICreateButton onClick={handleCreateCampaignClick}>
                      Create Campaign
                      </UICreateButton>
                    </div>
                  </Grid>
                </Grid>
              }
            </Grid>

            <TableContainer 
                  data={data} 
                  page={page} 
                  headers={headers}
                  sort={sort}
                  handleSort={handleSort}
                  handleChangePage={handleChangePage}
                  handleChangeRows={setRowsPerPage}
                  handleSelectPolicy={handleSelectPolicy}
                  handleExportAll={handleExportAll}
                  handleExport={handleExport}
                  exportOptions={runs}
                  loaded={loaded}
                  editable={isBroker}
                  divided={divided}
                  divideIndex={divider}
                  userType={userType}
                  renderRow={renderRow}
                  frozenColumns={frozenColumns}
                  frozenPositions={isBroker ? frozenGridColumnsPositionsBroker : frozenGridColumnsPositions}
                  hiddenColumns={hiddenColumns}
                  rowsPerPage={rowsPerPage}
                  filters={prospectsFilters}
            />
            {
              !isBroker &&
              <Grid container item className={classes.totalData}>
                <p className="page-data">
                    Prospects Within Your Selected Criteria: <NumberFormat value={data.count} displayType="text" thousandSeparator/>
                </p> 
                <p className="page-data">
                    {isAnnuities ? "With Total AUM" : "With Total Premium"}:<span>{` ${nFormatter(data.total_premium, 2, currency)}`}</span>
                </p>
              </Grid>
            }
                   

            <ColumnsPopup
              open={openColumns}
              options={columns}
              handleChange={onChangeColumns}
              handleClose={discardChangeColumns}
              handleApply={applyColumns}
            />          
        </>
    )
}

export default ProspectsContainer